import React, { useCallback, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import { DPIA_TRIGGER_SOURCE_OPTIONS } from 'config/api/dpiaRequests/dpiaRequests.constants';
import { buildProposedDpiaTitle } from 'config/api/dpiaRequests/dpiaRequests.helpers';
import type { DpiaRecommendation, DpiaRequestContext, DpiaRequestHistoryItem } from 'config/api/dpiaRequests/dpiaRequests.types';
import { getRopaCountriesForUi } from 'config/dictionaries/ropaCountryMasterdata';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { canAccessDpiaNeedAssessmentPage, canEditDpiaNeedAssessment } from '../_shared/assetRopaReviewAccess';
import DpiaHistoryList from '../_shared/DpiaHistoryList';
import QueryUnavailableState from '../_shared/QueryUnavailableState';
import useDpiaHistory from '../_shared/useDpiaHistory';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import {
  buildDpiaNeedAssessmentLocation,
  formatCountryNames,
  formatHistoryDate,
  formatHistoryItem,
  getActivePurposeSummary,
  getAxiosStatus,
  getCriterionLabel,
  getExistingDpiaIdFromError,
  getLiveDecisionDeviation,
  getLiveRecommendation,
  hasValidationErrors,
  isDpiaRequestEditable,
  isDpiaRequestNotEditableError,
  isMissingOperationalOwnerError,
  mapDpiaFieldErrorsFromApi,
  pickCurrentDpiaIdFromHistory,
  validateDecisionForm,
  validateDraftForm,
} from './dpiaNeedAssessment.helpers';
import useDpiaNeedAssessment from './useDpiaNeedAssessment';

type RouteParams = {
  apaId: string;
};

type LocationState = {
  returnPath?: string;
  requestId?: number;
  dpiaId?: number;
  sourceAssetProcessingActivityReviewId?: number;
  sourceRopaReviewRoundId?: number;
};

const getRecommendationBadgeClass = (recommendation: DpiaRecommendation, styles: ReturnType<typeof useStyles>) => {
  if (recommendation === 'dpia_strongly_recommended' || recommendation === 'dpia_recommended') {
    return styles.metricBadgeDanger;
  }

  if (recommendation === 'further_assessment_recommended') {
    return styles.metricBadgeWarn;
  }

  return styles.metricBadgeInfo;
};

const parseRequestId = (location: { search: string; state?: LocationState }): number | undefined => {
  if (typeof location.state?.requestId === 'number') {
    return location.state.requestId;
  }

  const fromQuery = new URLSearchParams(location.search).get('requestId');
  if (!fromQuery) {
    return undefined;
  }

  const parsed = Number(fromQuery);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const DpiaNeedAssessmentPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { apaId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessDpiaNeedAssessmentPage(userData);
  const canEditAsAdmin = canEditDpiaNeedAssessment(userData);
  const requestId = parseRequestId(location);
  const messages = processing_activities_messages.asset_ropa.dpia_need_assessment;
  const apaEditorMessages = processing_activities_messages.asset_ropa.apa_editor.sections.security.minimum_assessment_options;

  usePageTitle(t(messages.page_title));

  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});
  const [hasSubmittedDecision, setHasSubmittedDecision] = useState(false);

  const returnPath = location.state?.returnPath ?? PATHS.PROCESSING_ACTIVITIES_ASSET_REVIEWS;

  const validationMessages = useMemo(
    () => ({
      required: t(validation_messages.required),
      triggerComment: t(messages.validation.trigger_comment),
      alwaysRuleComment: t(messages.validation.always_rule_comment),
      decisionComment: t(messages.validation.decision_comment),
      decisionRequired: t(messages.validation.decision_required),
    }),
    [messages, t],
  );

  const handleClose = useCallback(() => {
    history.push(returnPath);
  }, [history, returnPath]);

  const handleOpenHistoryItem = useCallback(
    (item: DpiaRequestHistoryItem) => {
      if (item.kind === 'dpia') {
        history.push({
          pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, { dpiaId: String(item.id) }),
          state: { returnPath: history.location.pathname },
        });
        return;
      }

      history.push(
        buildDpiaNeedAssessmentLocation({
          apaId,
          requestId: item.id,
          returnPath,
        }),
      );
    },
    [apaId, history, returnPath],
  );

  const {
    allowNextNavigation,
    alreadyInProgress,
    context,
    criteria,
    decideNotRequiredMutation,
    decideRequiredMutation,
    envelopeError,
    formValues,
    handleNavigation,
    isDeciding,
    isError,
    isLoading,
    isSaving,
    NavigationPrompt,
    patchFormValues,
    refetch,
    request,
    saveMutation,
    toggleCriterion,
  } = useDpiaNeedAssessment({
    apaId: Number(apaId),
    onDecideSuccess: () => {
      enqueueSnackbar(t(messages.toasts.decided), { variant: 'success' });
      setHasSubmittedDecision(true);
    },
    onNavigate: handleClose,
    onSaveSuccess: () => {
      enqueueSnackbar(t(messages.toasts.saved), { variant: 'success' });
    },
    openInput: {
      sourceAssetProcessingActivityReviewId: location.state?.sourceAssetProcessingActivityReviewId,
      sourceRopaReviewRoundId: location.state?.sourceRopaReviewRoundId,
    },
    organizationId,
    requestId,
    useOpenEndpoint: canEditAsAdmin && !requestId,
  });

  const dpiaIdFromState = typeof location.state?.dpiaId === 'number' ? location.state.dpiaId : null;
  const dpiaIdFromError = getExistingDpiaIdFromError(envelopeError);
  const knownDpiaId = dpiaIdFromError ?? dpiaIdFromState;

  const dpiaHistoryQuery = useDpiaHistory({
    apaId: Number(apaId),
    organizationId,
  });

  const existingDpiaId = knownDpiaId ?? pickCurrentDpiaIdFromHistory(dpiaHistoryQuery.history?.items ?? []);

  const countryNameByCode = useMemo(() => new Map(getRopaCountriesForUi().map(country => [country.iso2, country.name])), []);
  const envelopeStatus = getAxiosStatus(envelopeError);
  const editable = Boolean(canEditAsAdmin && request && isDpiaRequestEditable(request.status) && !hasSubmittedDecision);
  const liveRecommendation = formValues ? getLiveRecommendation(formValues) : 'normally_not_recommended';
  const decisionDeviates = formValues ? getLiveDecisionDeviation(formValues) : false;
  const showSummary = Boolean(formValues?.decision && formValues.decisionComment.trim());
  const proposedDpiaTitle = context ? buildProposedDpiaTitle(context) : '';

  const canSaveDraft = useMemo(() => {
    if (!formValues) return false;
    return !hasValidationErrors(validateDraftForm(formValues, validationMessages));
  }, [formValues, validationMessages]);

  const saveDisabled = !canSaveDraft || isSaving || isDeciding || !editable;
  const saveDisabledTooltip = !canSaveDraft ? t(messages.validation.save_blocked) : '';
  const confirmDisabled = isDeciding || isSaving || hasSubmittedDecision || !editable;

  const handleMutationError = useCallback(
    async (error: unknown) => {
      if (isMissingOperationalOwnerError(error)) {
        enqueueSnackbar(t(messages.errors.missing_operational_owner), { variant: 'error' });
        return;
      }

      if (isDpiaRequestNotEditableError(error)) {
        enqueueSnackbar(t(messages.errors.not_editable), { variant: 'warning' });
        await refetch();
        return;
      }

      const fieldErrors = mapDpiaFieldErrorsFromApi(error);
      if (hasValidationErrors(fieldErrors)) {
        setValidationErrors(fieldErrors);
        enqueueSnackbar(t(messages.validation.save_blocked), { variant: 'warning' });
        return;
      }

      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    },
    [enqueueSnackbar, messages, refetch, t],
  );

  const handleSave = useCallback(async () => {
    if (!formValues || !canSaveDraft || !editable) return;

    const errors = validateDraftForm(formValues, validationMessages);
    setValidationErrors(errors);

    if (hasValidationErrors(errors)) {
      enqueueSnackbar(t(messages.validation.save_blocked), { variant: 'warning' });
      return;
    }

    try {
      await saveMutation.mutateAsync(formValues);
    } catch (error) {
      await handleMutationError(error);
    }
  }, [canSaveDraft, editable, enqueueSnackbar, formValues, handleMutationError, messages, saveMutation, t, validationMessages]);

  const handleConfirmDecision = useCallback(async () => {
    if (!formValues || !editable || isDeciding || hasSubmittedDecision) return;

    const errors = validateDecisionForm(formValues, validationMessages);
    setValidationErrors(errors);

    if (hasValidationErrors(errors)) {
      enqueueSnackbar(t(messages.validation.save_blocked), { variant: 'warning' });
      return;
    }

    try {
      if (formValues.decision === 'dpia_required') {
        await decideRequiredMutation.mutateAsync(formValues);
      } else {
        await decideNotRequiredMutation.mutateAsync(formValues);
      }
      allowNextNavigation();
    } catch (error) {
      await handleMutationError(error);
    }
  }, [
    allowNextNavigation,
    decideNotRequiredMutation,
    decideRequiredMutation,
    editable,
    enqueueSnackbar,
    formValues,
    handleMutationError,
    hasSubmittedDecision,
    isDeciding,
    messages,
    t,
    validationMessages,
  ]);

  const renderEditableActions = useCallback(
    () => (
      <>
        <Tooltip title={saveDisabledTooltip}>
          <Box className={styles.tooltipTrigger} component='span'>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={saveDisabled}
              icon={false}
              onClick={handleSave}
              showLoader={isSaving}
              textLike={false}
              variant='outlined'
            >
              {t(messages.save_and_continue)}
            </ColoredButton>
          </Box>
        </Tooltip>
        {formValues?.decision === 'dpia_required' && (
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={confirmDisabled}
            icon={false}
            onClick={handleConfirmDecision}
            showLoader={isDeciding}
            textLike={false}
            variant='contained'
          >
            {t(messages.sections.decision.confirm_required)}
          </ColoredButton>
        )}
        {formValues?.decision === 'dpia_not_required' && (
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={confirmDisabled}
            icon={false}
            onClick={handleConfirmDecision}
            showLoader={isDeciding}
            textLike={false}
            variant='contained'
          >
            {t(messages.sections.decision.confirm_not_required)}
          </ColoredButton>
        )}
      </>
    ),
    [
      confirmDisabled,
      formValues?.decision,
      handleConfirmDecision,
      handleSave,
      isDeciding,
      isSaving,
      messages,
      saveDisabled,
      saveDisabledTooltip,
      styles.tooltipTrigger,
      t,
    ],
  );

  const renderCategoryChips = (items: DpiaRequestContext['dataSubjectCategories']) => {
    if (items.length === 0) {
      return null;
    }

    return (
      <div className={styles.categoryTagRow}>
        {items.map(item => (
          <Chip key={item.id} className={styles.categoryChip} label={item.name} size='small' />
        ))}
      </div>
    );
  };

  const renderMinimumSecurity = (value: string) => {
    const option = apaEditorMessages[value as keyof typeof apaEditorMessages];
    return option ? t(option) : value.replace(/_/g, ' ');
  };

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.forbidden_body)}
      </Typography>
    );
  } else if (!canEditAsAdmin && !requestId) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(messages.errors.missing_request)}
      </Typography>
    );
  } else if (alreadyInProgress) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='textSecondary' variant='body2'>
          {t(messages.errors.already_in_progress)}
        </Typography>
        {existingDpiaId && (
          <div className={styles.centeredPageActions}>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={false}
              icon={false}
              onClick={() =>
                history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, { dpiaId: String(existingDpiaId) }), {
                  returnPath: history.location.pathname,
                })
              }
              showLoader={false}
              textLike={false}
              variant='outlined'
            >
              {t(messages.created_dpia.open)}
            </ColoredButton>
            {canEditAsAdmin && (
              <ColoredButton
                className=''
                customColor='grey'
                disabled={false}
                icon={false}
                onClick={() =>
                  history.push(generatePath(PATHS.PROCESSING_ACTIVITIES_ADMIN_DPIA_REVIEW, { dpiaId: String(existingDpiaId) }), {
                    returnPath: history.location.pathname,
                  })
                }
                showLoader={false}
                textLike={false}
                variant='outlined'
              >
                {t(processing_activities_messages.asset_ropa.apa_review.open_admin_dpia_review)}
              </ColoredButton>
            )}
          </div>
        )}
      </Box>
    );
  } else if (envelopeStatus === 403) {
    content = (
      <QueryUnavailableState
        backLabel={t(general_messages.back)}
        message={t(processing_activities_messages.asset_ropa.forbidden_body)}
        onBack={handleClose}
      />
    );
  } else if (envelopeStatus === 404) {
    content = <QueryUnavailableState backLabel={t(general_messages.back)} message={t(messages.errors.not_found)} onBack={handleClose} />;
  } else if (envelopeStatus === 409) {
    content = <QueryUnavailableState backLabel={t(general_messages.back)} message={t(messages.errors.not_editable)} onBack={handleClose} />;
  } else if (isLoading || (canAccess && !isError && (!formValues || !request || !context))) {
    content = <Loader inner />;
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else if (formValues && request && context) {
    const pageSubtitle = `${context.processingActivityName} · ${context.assetName}`;

    content = (
      <>
        <Box className={styles.reviewPanel}>
          <div className={styles.reviewPanelHeader}>
            <div>
              <div className={styles.reviewEyebrow}>{t(messages.statuses[request.status]).toUpperCase()}</div>
              <Typography variant='h3'>{t(messages.banner_title)}</Typography>
              <Typography color='textSecondary' variant='body2'>
                {pageSubtitle}
              </Typography>
              <div className={styles.reviewMeta}>
                <span>
                  <strong>{t(messages.request_status)}:</strong>{' '}
                  <span className={`${styles.statusBadge} ${styles.statusBadgeWarn}`}>{t(messages.statuses[request.status])}</span>
                </span>
                <span>
                  <strong>{t(messages.asset_owner)}:</strong> {context.assetOwnerName}
                </span>
                <span>
                  <strong>{t(messages.dpia_indication)}:</strong>{' '}
                  {(() => {
                    const indicationMessages = processing_activities_messages.asset_ropa.dpia_status as Record<string, string[]>;
                    const indicationMessage = indicationMessages[context.dpiaIndicationStatus];
                    return indicationMessage ? t(indicationMessage) : context.dpiaIndicationStatus.replace(/_/g, ' ');
                  })()}
                </span>
              </div>
            </div>
            <div className={styles.metricsRowTwo}>
              <Box className={styles.metricTile}>
                <Box className={styles.metricCard}>
                  <div className={styles.metricLabel}>{t(messages.previous_requests)}</div>
                  <div className={styles.metricValue}>{context.previousRequestsCount}</div>
                </Box>
              </Box>
              <Box className={styles.metricTile}>
                <Box className={styles.metricCard}>
                  <div className={styles.metricLabel}>{t(messages.previous_dpias)}</div>
                  <div className={styles.metricValue}>{context.previousDpiasCount}</div>
                </Box>
              </Box>
            </div>
          </div>
        </Box>

        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(messages.truth_note)}
        </Typography>

        <div className={styles.twoColumnLayout}>
          <aside>
            <Box className={styles.sidePanel}>
              <div className={styles.reviewPanelHeader}>
                <div>
                  <div className={styles.reviewEyebrow}>{t(messages.side_panel.eyebrow)}</div>
                  <Typography variant='h3'>{t(messages.side_panel.title)}</Typography>
                </div>
                {context.processingActivityStatus === 'published' && (
                  <span className={`${styles.statusBadge} ${styles.statusBadgeSuccess}`}>{t(messages.side_panel.active)}</span>
                )}
              </div>
              <div className={styles.fieldList}>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.asset)}</div>
                  <div>{context.assetName}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.processing_activity)}</div>
                  <div>{context.processingActivityName}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.asset_owner)}</div>
                  <div>{context.assetOwnerName}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.data_subjects)}</div>
                  <div>{renderCategoryChips(context.dataSubjectCategories)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.personal_data)}</div>
                  <div>{renderCategoryChips(context.personalDataCategories)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.active_purposes)}</div>
                  <div>{getActivePurposeSummary(context)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.countries)}</div>
                  <div>{formatCountryNames(context.countrySummary.countryCodes, countryNameByCode)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(messages.side_panel.minimum_security)}</div>
                  <div>{renderMinimumSecurity(context.minimumSecurityAssessment)}</div>
                </div>
              </div>
              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.side_panel.indicators)}
              </Typography>
              {context.indicators.thirdCountryAccess && (
                <Typography variant='body2'>{t(messages.side_panel.third_country_access)}</Typography>
              )}
              {context.indicators.dpiaIndicated && <Typography variant='body2'>{t(messages.side_panel.dpia_indicated)}</Typography>}
              {context.indicators.minimumSecurityPartiallyMet && (
                <Typography variant='body2'>{t(messages.side_panel.minimum_security_partial)}</Typography>
              )}
              {context.indicators.sensitivePersonalDataFlagged && (
                <Typography variant='body2'>{t(messages.side_panel.sensitive_personal_data)}</Typography>
              )}
              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.history_title)}
              </Typography>
              {context.history.map(item => {
                const formatted = formatHistoryItem(item, t, {
                  need_assessment: messages.history_need_assessment,
                  decision: messages.history_decision,
                  status: messages.history_status,
                  statuses: messages.statuses,
                });
                return (
                  <Box key={`${item.kind}-${item.id}`} style={{ marginTop: 8 }}>
                    <TextButton onClick={() => handleOpenHistoryItem(item)}>
                      <Typography variant='body2'>
                        <strong>{formatted.title}</strong>
                      </Typography>
                    </TextButton>
                    <Typography color='textSecondary' variant='body2'>
                      {formatted.subtitle}
                    </Typography>
                    {item.kind === 'dpia_request' && item.indicatedAt && (
                      <Typography color='textSecondary' variant='body2'>
                        {t(messages.history_indicated_on, { date: formatHistoryDate(item.indicatedAt) })}
                      </Typography>
                    )}
                    {item.kind === 'dpia_request' && item.indicationComment && (
                      <Typography variant='body2'>{item.indicationComment}</Typography>
                    )}
                  </Box>
                );
              })}
              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.dpia_versions_title)}
              </Typography>
              {dpiaHistoryQuery.history && (
                <DpiaHistoryList
                  items={dpiaHistoryQuery.history.items}
                  onOpenItem={item =>
                    handleOpenHistoryItem({
                      ...item,
                      kind: 'dpia',
                      assessedAt: null,
                      indicatedAt: null,
                      indicationComment: null,
                    })
                  }
                />
              )}
            </Box>
          </aside>

          <section className={styles.mainPanel}>
            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 1</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.trigger.title)}
              </Typography>
              <FormControl disabled={!editable} fullWidth margin='normal' variant='outlined'>
                <InputLabel>{t(messages.sections.trigger.source)}</InputLabel>
                <Select
                  label={t(messages.sections.trigger.source)}
                  onChange={event => patchFormValues({ triggerSource: event.target.value as typeof formValues.triggerSource })}
                  value={formValues.triggerSource}
                >
                  {DPIA_TRIGGER_SOURCE_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {t(messages.trigger_sources[option])}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.triggerComment)}
                fullWidth
                helperText={validationErrors.triggerComment}
                label={t(messages.sections.trigger.comment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ triggerComment: event.target.value })}
                rows={4}
                value={formValues.triggerComment}
                variant='outlined'
              />
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewPanelHeader}>
                <div>
                  <div className={styles.reviewEyebrow}>SECTION 2</div>
                  <Typography gutterBottom variant='h3'>
                    {t(messages.sections.criteria.title)}
                  </Typography>
                </div>
                <Chip label={t(messages.sections.criteria.selected_count, { count: formValues.selectedCriteria.length })} />
              </div>
              <Typography className={styles.pageHelp} color='textSecondary' gutterBottom variant='body2'>
                {t(messages.sections.criteria.explanation)}
              </Typography>
              <div className={`${styles.assetList} ${styles.apaSectionBody}`}>
                {criteria.map(criterion => (
                  <Box key={criterion.code} className={styles.purposePanel}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.selectedCriteria.includes(criterion.code)}
                          color='primary'
                          disabled={!editable}
                          onChange={() => toggleCriterion(criterion.code)}
                        />
                      }
                      label={
                        <span>
                          <Typography variant='body2'>
                            <strong>{getCriterionLabel(criterion, i18n.language)}</strong>
                          </Typography>
                          {criterion.code === 'other_high_risk_criterion' && (
                            <Typography color='textSecondary' variant='caption'>
                              {t(messages.sections.criteria.criterion_ten_help)}
                            </Typography>
                          )}
                        </span>
                      }
                    />
                  </Box>
                ))}
              </div>
              {validationErrors.selectedCriteria && (
                <FormHelperText error style={{ marginTop: 8 }}>
                  {validationErrors.selectedCriteria}
                </FormHelperText>
              )}
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.criteriaAssessmentComment)}
                fullWidth
                helperText={validationErrors.criteriaAssessmentComment}
                label={t(messages.sections.criteria.comment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ criteriaAssessmentComment: event.target.value })}
                rows={4}
                value={formValues.criteriaAssessmentComment}
                variant='outlined'
              />
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 3</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.always_rule.title)}
              </Typography>
              <Box className={`${styles.purposePanel} ${styles.apaSectionBody}`}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.alwaysDpiaRuleTriggered}
                      color='primary'
                      disabled={!editable}
                      onChange={event =>
                        patchFormValues({
                          alwaysDpiaRuleTriggered: event.target.checked,
                          alwaysDpiaRuleComment: event.target.checked ? formValues.alwaysDpiaRuleComment : '',
                        })
                      }
                    />
                  }
                  label={
                    <span>
                      <Typography variant='body2'>
                        <strong>{t(messages.sections.always_rule.checkbox)}</strong>
                      </Typography>
                      <Typography color='textSecondary' variant='caption'>
                        {t(messages.sections.always_rule.help)}
                      </Typography>
                    </span>
                  }
                />
              </Box>
              <TextField
                disabled={!editable || !formValues.alwaysDpiaRuleTriggered}
                error={Boolean(validationErrors.alwaysDpiaRuleComment)}
                fullWidth
                helperText={validationErrors.alwaysDpiaRuleComment}
                label={t(messages.sections.always_rule.comment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ alwaysDpiaRuleComment: event.target.value })}
                rows={3}
                value={formValues.alwaysDpiaRuleComment}
                variant='outlined'
              />
            </Box>

            <Box className={`${styles.apaSection} ${styles.reviewPanel}`}>
              <div className={styles.reviewPanelHeader}>
                <div>
                  <div className={styles.reviewEyebrow}>SECTION 4</div>
                  <Typography gutterBottom variant='h3'>
                    {t(messages.sections.recommendation.title)}
                  </Typography>
                </div>
                <span className={`${styles.metricBadge} ${getRecommendationBadgeClass(liveRecommendation, styles)}`}>
                  {t(messages.recommendations[liveRecommendation])}
                </span>
              </div>
              <div className={clsx(styles.apaSectionContent, styles.apaSectionBody)}>
                <div className={styles.metricsRowAuto}>
                  {[
                    {
                      key: 'selected_criteria',
                      label: t(messages.sections.recommendation.selected_criteria),
                      multiline: false,
                      value: String(formValues.selectedCriteria.length),
                    },
                    {
                      key: 'always_rule',
                      label: t(messages.sections.recommendation.always_rule),
                      multiline: false,
                      value: formValues.alwaysDpiaRuleTriggered ? t(messages.yes) : t(messages.no),
                    },
                    {
                      key: 'recommendation',
                      label: t(messages.sections.recommendation.title),
                      multiline: true,
                      value: t(messages.recommendations[liveRecommendation]),
                    },
                  ].map(metric => (
                    <Box key={metric.key} className={styles.metricTile}>
                      <Box className={styles.metricCard}>
                        <div className={styles.metricLabel}>{metric.label}</div>
                        <div className={metric.multiline ? styles.metricValueMultiline : styles.metricValue}>{metric.value}</div>
                      </Box>
                    </Box>
                  ))}
                </div>
                <Box className={styles.reviewBanner}>
                  <Typography variant='body2'>{t(messages.sections.recommendation.notice)}</Typography>
                </Box>
              </div>
            </Box>

            <Box className={clsx(styles.apaSection, styles.adminDpiaReviewDecisionSection)}>
              <div className={styles.apaSectionHeader}>
                <Typography className={styles.apaSectionTitle} variant='h3'>
                  {t(messages.sections.decision.title)}
                </Typography>
              </div>
              <div className={clsx(styles.apaSectionContent, styles.apaSectionFields)}>
                <FormControl component='fieldset' disabled={!editable} error={Boolean(validationErrors.decision)} fullWidth>
                  <RadioGroup
                    aria-label={t(messages.sections.decision.title)}
                    className={styles.reviewDecisionOptions}
                    onChange={event => patchFormValues({ decision: event.target.value as typeof formValues.decision })}
                    value={formValues.decision}
                  >
                    <Box className={styles.purposePanel}>
                      <FormControlLabel
                        control={<Radio color='primary' />}
                        label={t(messages.sections.decision.required)}
                        value='dpia_required'
                      />
                    </Box>
                    <Box className={styles.purposePanel}>
                      <FormControlLabel
                        control={<Radio color='primary' />}
                        label={t(messages.sections.decision.not_required)}
                        value='dpia_not_required'
                      />
                    </Box>
                  </RadioGroup>
                  {validationErrors.decision && <FormHelperText>{validationErrors.decision}</FormHelperText>}
                </FormControl>
                <TextField
                  disabled={!editable}
                  error={Boolean(validationErrors.decisionComment)}
                  fullWidth
                  helperText={validationErrors.decisionComment}
                  label={t(messages.sections.decision.comment)}
                  margin='none'
                  multiline
                  onChange={event => patchFormValues({ decisionComment: event.target.value })}
                  rows={4}
                  value={formValues.decisionComment}
                  variant='outlined'
                />
                {decisionDeviates && editable && (
                  <Box className={styles.reviewBanner}>
                    <Typography variant='body2'>{t(messages.sections.decision.deviation_warning)}</Typography>
                  </Box>
                )}

                {showSummary && (
                  <Box className={styles.closureBanner}>
                    <Typography gutterBottom variant='h4'>
                      {t(messages.sections.decision.summary_title)}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>{t(messages.sections.recommendation.selected_criteria)}:</strong> {formValues.selectedCriteria.length}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>{t(messages.sections.recommendation.always_rule)}:</strong>{' '}
                      {formValues.alwaysDpiaRuleTriggered ? t(messages.yes) : t(messages.no)}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>{t(messages.sections.recommendation.title)}:</strong> {t(messages.recommendations[liveRecommendation])}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>{t(messages.sections.decision.title)}:</strong>{' '}
                      {formValues.decision === 'dpia_required'
                        ? t(messages.sections.decision.required)
                        : t(messages.sections.decision.not_required)}
                    </Typography>
                    {formValues.decision === 'dpia_required' && (
                      <>
                        <Typography variant='body2'>
                          <strong>{t(messages.sections.decision.responsible_owner)}:</strong> {context.assetOwnerName}
                        </Typography>
                        <Typography variant='body2'>
                          <strong>{t(messages.sections.decision.created_dpia_title)}:</strong> {proposedDpiaTitle}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}

                {request.createdDpiaId && (
                  <Box className={styles.closureBanner}>
                    <Typography gutterBottom variant='h4'>
                      {t(messages.created_dpia.title)}
                    </Typography>
                    <Typography variant='body2'>
                      <strong>{proposedDpiaTitle}</strong>
                    </Typography>
                    <Button
                      onClick={() =>
                        history.push({
                          pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, {
                            dpiaId: String(request.createdDpiaId),
                          }),
                          state: { returnPath: history.location.pathname },
                        })
                      }
                      style={{ marginTop: 12 }}
                      variant='outlined'
                    >
                      {t(messages.created_dpia.open)}
                    </Button>
                  </Box>
                )}

                {editable && <Box className={clsx(styles.formActions, styles.formActionsSectionFooter)}>{renderEditableActions()}</Box>}
              </div>
            </Box>
          </section>
        </div>
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          backButton
          noBorder
          onBack={() => handleNavigation()}
          subtitle={context ? `${context.processingActivityName} · ${context.assetName}` : t(messages.page_subtitle)}
        >
          {t(messages.page_title)}
        </ManagePageHeader>
        {content}
        <NavigationPrompt />
      </Container>
    </ErrorBoundary>
  );
};

export default DpiaNeedAssessmentPage;
